// Notifications: TopBar bell + popover (DESIGN §2). Items come from derive.notificationsFor for the current person;
// unread = newer than localStorage 'frazier-notif-seen:<personId>'. Max 8 items + "Mark all read".
import './notifications.css';
import { useMemo, useState } from 'preact/hooks';
import * as derive from '../../shared/derive.js';
import { useStore } from '../store.js';
import { Icon } from '../icons.jsx';
import { ActivityItem } from './activity-item.jsx';
import { Popover, usePopover } from './popover.jsx';
import { cx } from './ui.jsx';

const SEEN_KEY = 'frazier-notif-seen';
const MAX = 8;

// kind → [icon, tone] (tone: accent | positive | warning | danger | neutral)
const KIND_GLYPH = {
  assigned: ['user', 'accent'],
  review: ['inbox', 'warning'],
  dueToday: ['clock', 'warning'],
  due: ['clock', 'warning'],
  overdue: ['clock', 'danger'],
  completed: ['checkCircle', 'positive'],
  batchDone: ['checkCircle', 'positive'],
  blocked: ['alert', 'danger'],
};

// One marker per person ('frazier-notif-seen:<id>'): people sharing a browser never mark each other's bell read.
const seenKey = (personId) => `${SEEN_KEY}:${personId}`;

function readSeen(personId) {
  if (!personId) return '';
  try {
    const store = window.localStorage;
    const own = store.getItem(seenKey(personId));
    if (own != null) return own;
    // Before per-person markers there was one per browser: the first person to read it adopts it, once.
    const legacy = store.getItem(SEEN_KEY);
    if (legacy == null) return '';
    store.removeItem(SEEN_KEY);
    store.setItem(seenKey(personId), legacy);
    return legacy;
  } catch {
    return '';
  }
}

function writeSeen(personId, value) {
  try {
    if (personId) window.localStorage.setItem(seenKey(personId), value);
  } catch {
    // Storage blocked: read state lasts for this page view only.
  }
}

// → { items: [...], unread: n } for the current person. Never throws (the shared function may be mid-rewrite).
export function useNotifications(seen) {
  const { state, index, today, currentPerson } = useStore();
  let items = [];
  if (state && index && currentPerson && derive.notificationsFor) {
    try {
      items = derive.notificationsFor(state, index, currentPerson, today) || [];
    } catch {
      items = [];
    }
  }
  const isUnread = (n) => !!n.at && String(n.at) > seen;
  return { items, unread: items.filter(isUnread).length, isUnread, today, currentPerson };
}

export function NotificationsButton({ class: cls }) {
  const pop = usePopover();
  const person = useStore().currentPerson;
  const stored = useMemo(() => readSeen(person), [person]);
  const [marked, setMarked] = useState(null); // { person, at } after "Mark all read" in this page view
  const seen = marked?.person === person ? marked.at : stored;
  const { items, unread, isUnread, today, currentPerson } = useNotifications(seen);
  const markAll = () => {
    const now = new Date().toISOString();
    writeSeen(person, now);
    setMarked({ person, at: now });
  };
  const label = unread ? `Notifications (${unread} unread)` : 'Notifications';
  return (
    <>
      <button ref={pop.anchorRef} type="button" class={cx('icon-btn', 'bell', cls)} aria-label={label} title="Notifications" {...pop.triggerProps}>
        <Icon name="bell" size={18} />
        {unread > 0 && <span class="bell-dot" aria-hidden="true" />}
      </button>
      <Popover open={pop.open} onClose={pop.close} anchorRef={pop.anchorRef} width={360} label="Notifications" class="notif-pop">
        <div class="notif-head">
          <span class="notif-title">Notifications</span>
          {unread > 0 && <button type="button" class="notif-mark" onClick={markAll}>Mark all read</button>}
        </div>
        {!currentPerson ? (
          <p class="notif-empty">Choose your name (avatar menu) to see your notifications.</p>
        ) : items.length === 0 ? (
          <p class="notif-empty">You're all caught up.</p>
        ) : (
          <div class="notif-list">
            {items.slice(0, MAX).map((n, i) => {
              const [icon, tone] = KIND_GLYPH[n.kind] || ['bell', 'neutral'];
              return (
                <ActivityItem
                  key={n.id || i}
                  icon={icon}
                  tone={n.tone || tone}
                  text={n.text}
                  at={n.at}
                  meta={n.kind === 'due' || n.kind === 'overdue' ? 'Deadline' : undefined}
                  today={today}
                  href={n.href}
                  unread={isUnread(n)}
                  onClick={n.href ? pop.close : undefined}
                />
              );
            })}
          </div>
        )}
      </Popover>
    </>
  );
}
